import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  UncontrolledTooltip,
} from "reactstrap";
import { useStaffStore } from "../../../stores/Staffs";
import moment from "moment";

const InvitationsModal = ({ isOpen, toggle }) => {
  const {
    getAllInvitations,
    cancelInvitation,
    resendInvitation,
    invitations,
    invitationsLoading,
    invitationsError,
  } = useStaffStore((state) => ({
    getAllInvitations: state.getAllInvitations,
    cancelInvitation: state.cancelInvitation,
    resendInvitation: state.resendInvitation,
    invitations: state.invitations,
    invitationsLoading: state.invitationsLoading,
    invitationsError: state.invitationsError,
  }));

  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (isOpen) {
      console.log("Fetching invitations...");
      getAllInvitations().then(data => {
        console.log("Invitations data: success");
      }).catch(error => {
        console.error("Error fetching invitations:", error);
        console.log("Invitations data: error");
      });
    }
  }, [isOpen, getAllInvitations]);

  const getStatusBadge = (status, expiresAt) => {
    const isExpired = new Date(expiresAt) < new Date();
    
    if (isExpired && status === "PENDING") {
      return <Badge color="warning">EXPIRED</Badge>;
    }

    switch (status) {
      case "PENDING":
        return <Badge color="info">PENDING</Badge>;
      case "ACCEPTED":
        return <Badge color="success">ACCEPTED</Badge>;
      case "DECLINED":
        return <Badge color="danger">DECLINED</Badge>;
      case "CANCELLED":
        return <Badge color="secondary">CANCELLED</Badge>;
      case "EXPIRED":
        return <Badge color="warning">EXPIRED</Badge>;
      default:
        return <Badge color="light">UNKNOWN</Badge>;
    }
  };

  const handleCancelInvitation = async (inviteId) => {
    try {
      setActionLoading(inviteId);
      await cancelInvitation(inviteId);
    } catch (error) {
      console.error("Error cancelling invitation:", error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendInvitation = async (inviteId) => {
    try {
      setActionLoading(inviteId);
      await resendInvitation(inviteId);
    } catch (error) {
      console.error("Error resending invitation:", error);
    } finally {
      setActionLoading(null);
    }
  };

  const canResend = (status, expiresAt) => {
    const isExpired = new Date(expiresAt) < new Date();
    return status === "PENDING" && !isExpired;
  };

  const canCancel = (status) => {
    return status === "PENDING";
  };

  const formatSpaceIds = (spaceIds) => {
    if (Array.isArray(spaceIds)) {
      return spaceIds.join(", ");
    }
    return "N/A";
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>
        <i className="ri-mail-line me-2"></i>
        Invitation Management
      </ModalHeader>
      <ModalBody>
        {invitationsLoading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading invitations...</p>
          </div>
        ) : invitationsError ? (
          <Alert color="danger">
            <i className="ri-error-warning-line me-2"></i>
            {invitationsError}
          </Alert>
        ) : invitations.length === 0 ? (
          <div className="text-center py-4">
            <i className="ri-mail-line text-muted" style={{ fontSize: "3rem" }}></i>
            <p className="text-muted mt-2">No invitations found</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Site</th>
                  <th>Spaces</th>
                  <th>Status</th>
                  <th>Invited By</th>
                  <th>Invited At</th>
                  <th>Expires At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => (
                  <tr key={invitation.invite_id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <i className="ri-mail-line me-2 text-muted"></i>
                        {invitation.email}
                      </div>
                    </td>
                    <td>
                      <span className="fw-medium">
                        {invitation.site?.name || "N/A"}
                      </span>
                    </td>
                    <td>
                      <span className="text-muted">
                        {formatSpaceIds(invitation.space_ids)}
                      </span>
                    </td>
                    <td>
                      <div>
                        {getStatusBadge(invitation.status, invitation.expires_at)}
                        {(invitation.status === "CANCELLED" || invitation.status === "DECLINED") && (
                          <div className="mt-1">
                            <small className="text-muted d-block">
                              {invitation.status === "CANCELLED" && invitation.cancelled_at && (
                                <>
                                  Cancelled: {moment(invitation.cancelled_at).format("MMM DD, YYYY HH:mm")}
                                  {invitation.cancelled_by_user && (
                                    <span className="d-block">
                                      by {invitation.cancelled_by_user.name || invitation.cancelled_by_user.email}
                                    </span>
                                  )}
                                </>
                              )}
                              {invitation.status === "DECLINED" && invitation.declined_at && (
                                <>
                                  Declined: {moment(invitation.declined_at).format("MMM DD, YYYY HH:mm")}
                                </>
                              )}
                            </small>
                          </div>
                        )}
                        {invitation.status === "ACCEPTED" && invitation.accepted_at && (
                          <div className="mt-1">
                            <small className="text-muted">
                              Accepted: {moment(invitation.accepted_at).format("MMM DD, YYYY HH:mm")}
                            </small>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="fw-medium">
                          {invitation.inviter?.name || invitation.inviter?.email || "N/A"}
                        </div>
                        {invitation.inviter?.email && invitation.inviter?.name && (
                          <small className="text-muted">
                            {invitation.inviter.email}
                          </small>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="text-muted">
                        {moment(invitation.invited_at).format("MMM DD, YYYY HH:mm")}
                      </span>
                    </td>
                    <td>
                      <span className="text-muted">
                        {moment(invitation.expires_at).format("MMM DD, YYYY HH:mm")}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        {canResend(invitation.status, invitation.expires_at) && (
                          <>
                            <Button
                              size="sm"
                              color="primary"
                              outline
                              onClick={() => handleResendInvitation(invitation.invite_id)}
                              disabled={actionLoading === invitation.invite_id}
                              id={`resend-${invitation.invite_id}`}
                            >
                              {actionLoading === invitation.invite_id ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="ri-send-plane-line"></i>
                              )}
                            </Button>
                            <UncontrolledTooltip target={`resend-${invitation.invite_id}`}>
                              Resend Invitation
                            </UncontrolledTooltip>
                          </>
                        )}
                        
                        {canCancel(invitation.status) && (
                          <>
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              onClick={() => handleCancelInvitation(invitation.invite_id)}
                              disabled={actionLoading === invitation.invite_id}
                              id={`cancel-${invitation.invite_id}`}
                            >
                              {actionLoading === invitation.invite_id ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="ri-close-line"></i>
                              )}
                            </Button>
                            <UncontrolledTooltip target={`cancel-${invitation.invite_id}`}>
                              Cancel Invitation
                            </UncontrolledTooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InvitationsModal;
